import { Badge, Box, IconButton, Tooltip, Typography } from "@mui/material";
import { ReduxState, useListContext } from "ra-core";
import { FC, useMemo } from "react";
import { Link as RouterLink } from 'react-router-dom'
import { RecPatch } from "~/dataProvider/tools";
import { Pagination as DefaultPagination } from '~/components/list/pagination'
import { FunctionField } from "../field";
import { Datagrid } from "../list/datagrid";
import Scrollbar from "../Scrollbar";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EditIcon from '@mui/icons-material/Edit';
import { useSelector } from "react-redux";
import PendingReviewIcon from "~/icons/PendingReview";
import SizeTextField from "../field/SizeTextField";
import { genUrlWithHashid } from "~/utils/hashid";
import { useUserTimeFormat } from "~/utils/userTimeFormat";
import { AtopFuncArea, SearchField } from "../list/datagridFunctionWidgets";
import { EncryptionMappingResponseDto } from "~/gen/api/governance";
import DatabaseNameField from "../databases/DatabaseNameField";
import { useHasPermit } from "~/authProvider/useHasPermit";
import { EDIT_DATA_CLASSIFICATION_ENCRYPTION } from "~/constants";
import { nonSqlDbTypes } from "./defs";


const DatabaseMappingUpdatesTable: FC = () => {
  const hasWritePermit = useHasPermit([EDIT_DATA_CLASSIFICATION_ENCRYPTION])
  const isRegistered = useSelector(
    (state: ReduxState) => !!state.admin.resources['mappings-update']
  )

  const listContext = useListContext()
  
  const format = useUserTimeFormat()

  const persistFilters = useMemo(() => ({
    existsPendingReview: (x: EncryptionMappingResponseDto) =>
      x.pendingReviewCount && x.pendingReviewCount > 0,
  }), [])

  return (
    !isRegistered ? null :
      <>
        <AtopFuncArea persistFilters={persistFilters}>
          <SearchField 
            sourceKey="dbName"
            match={(d: EncryptionMappingResponseDto, fval:string) => !!d.databaseName && d.databaseName.toLowerCase().includes(fval.trim().toLowerCase())}
            sx={{ width: 280 }}
            TextFieldProps={{ placeholder: 'Search by Database Name' }}
          />
        </AtopFuncArea>
        <Scrollbar>
          <Box sx={{ minWidth: 1150 }}>
            <Datagrid
              hasBulkActions={false} 
              {...listContext}
              sx={{ 
                '& th' : { py: 2 },
                '& td' : { py: 2 },
                '& tr div.MuiBox-root': {
                  '& div.encryptedDbTip': { top: theme => theme.spacing(-3.25) },
                  '& span.encryptedDbTipText': { top: theme => theme.spacing(-3.25) },
                },
              }}
            >
              <DatabaseNameField label="Database" keyMap={{ name: 'databaseName' }} />
              <SizeTextField source="sizeText" numericSource="size" label="Size" emptyText="N/A" />
              <FunctionField 
                label="Last Successful Backup"
                source="lastBackupTime"
                render={(d?: EncryptionMappingResponseDto & RecPatch) => (
                  <Typography
                    component="span"
                    variant="body2"
                  >
                    {d?.lastBackupTime 
                      ? format(d.lastBackupTime, 'Ppp')
                      : 'N/A'}
                  </Typography>
                )}
              />
              <FunctionField 
                label="Fields Pending Review" 
                sortable={false} 
                render={(d?: EncryptionMappingResponseDto & RecPatch) => (
                  <Tooltip title="Pending Review">
                    <IconButton color={"inherit" as any}>
                      <Badge color={"warning" as any} badgeContent={d?.pendingReviewCount || 0} showZero max={9999}
                        sx={{ 'span.MuiBadge-badge': {color: theme => theme.palette.mode === 'light' ? 'black!important' : undefined }}}
                      >
                        <PendingReviewIcon />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                )}
              />
              <FunctionField 
                label="Action"
                sortable={false}
                render={(d?: EncryptionMappingResponseDto & RecPatch) => (
                  <Tooltip title="Update Mappings">
                    <IconButton 
                      sx={{ 
                        color: 'text.primary' as any, 
                        '&:hover': { backgroundColor: 'action.hover' },
                      }}
                      component={RouterLink}
                      to={genUrlWithHashid(
                        nonSqlDbTypes.has(d!.databaseType!) 
                          ? `/encryption/mappings/$0/update/ns`
                          : `/encryption/mappings/$0/update`, 
                        d?.databaseId || 0, d?.databaseName
                      )}
                    >
                      {hasWritePermit ? <EditIcon fontSize="small" /> : <ArrowForwardIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                )}
              />
            </Datagrid>
          </Box>
        </Scrollbar>
        {//@ts-ignore 
        <DefaultPagination {...listContext} />}
      </>
  )
}

export default DatabaseMappingUpdatesTable;
